import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { FileText, RefreshCw, Search, Eye, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: 'new', label_en: 'New', label_ar: 'جديد', color: 'bg-blue-100 text-blue-800' },
  { key: 'under_review', label_en: 'Under Review', label_ar: 'قيد المراجعة', color: 'bg-amber-100 text-amber-800' },
  { key: 'accepted', label_en: 'Accepted', label_ar: 'مقبول', color: 'bg-green-100 text-green-800' },
  { key: 'rejected', label_en: 'Rejected', label_ar: 'مرفوض', color: 'bg-red-100 text-red-800' },
];

const StatusBadge = ({ status }) => {
  const cfg = STATUSES.find((s) => s.key === status) || STATUSES[0];
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${cfg.color}`} data-testid={`status-badge-${status}`}>
      {cfg.label_en}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
};

const ApplicationsTab = ({ type }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (query.trim()) params.q = query.trim();
      const r = await axios.get(`${API}/admin/applications`, { params, withCredentials: true });
      setItems(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [type, statusFilter, query]);

  useEffect(() => { load(); }, [load]);

  const downloadCV = (app) => {
    // Cookies carry auth; open in a new tab so browser saves the file.
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/applications/${app.id}/cv`, '_blank');
  };

  const changeStatus = async (app, newStatus) => {
    if (app.status === newStatus) return;
    setSavingStatus(true);
    try {
      const r = await axios.patch(
        `${API}/admin/applications/${app.id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      setItems((xs) => xs.map((x) => (x.id === app.id ? r.data : x)));
      if (selected && selected.id === app.id) setSelected(r.data);
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingStatus(false);
    }
  };

  const remove = async (app) => {
    if (!window.confirm('Delete this application permanently?')) return;
    try {
      await axios.delete(`${API}/admin/applications/${app.id}`, { withCredentials: true });
      setItems((xs) => xs.filter((x) => x.id !== app.id));
      if (selected && selected.id === app.id) setSelected(null);
      toast.success('Deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const filterButtons = useMemo(() => [
    { key: 'all', label: 'All' },
    ...STATUSES.map((s) => ({ key: s.key, label: s.label_en })),
  ], []);

  return (
    <div className="space-y-4" data-testid={`applications-tab-${type}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search name, email, phone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid={`search-${type}`}
          />
        </div>
        {filterButtons.map((b) => (
          <button
            key={b.key}
            onClick={() => setStatusFilter(b.key)}
            data-testid={`filter-status-${type}-${b.key}`}
            className={`px-3 py-1 rounded-full text-xs font-bold border transition
              ${statusFilter === b.key ? 'bg-[#1e88e5] text-white border-[#1e88e5]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
          >
            {b.label}
          </button>
        ))}
        <button onClick={load} className="p-1 rounded-full hover:bg-gray-100" data-testid={`reload-${type}`}>
          <RefreshCw size={16} />
        </button>
      </div>

      {loading && <div className="text-center text-gray-500 py-8">Loading…</div>}
      {!loading && items.length === 0 && (
        <Card className="p-8 text-center text-gray-500" data-testid={`empty-${type}`}>No applications found</Card>
      )}

      {!loading && items.map((app) => (
        <Card key={app.id} className="p-4" data-testid={`application-row-${app.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-bold text-[#0d2c54]">{app.full_name}</span>
                <StatusBadge status={app.status} />
                <span className="text-xs text-gray-500">{formatDate(app.created_at)}</span>
              </div>
              <div className="text-sm text-gray-700">
                {type === 'doctor_pharmacist' ? app.specialty : app.job_title} · {app.highest_qualification} · {app.years_of_experience} yr exp
              </div>
              <div className="text-xs text-gray-500 mt-1">
                <span dir="ltr">{app.phone_number}</span> · <span dir="ltr">{app.email}</span>
                {app.location ? ` · ${app.location}` : ''}
              </div>
            </div>

            <div className="flex flex-col gap-2 min-w-[220px]">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSelected(app)} data-testid={`view-application-${app.id}`}>
                  <Eye size={14} className="mr-1" /> View
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadCV(app)} data-testid={`download-cv-${app.id}`}>
                  <FileText size={14} className="mr-1" /> CV
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(app)} data-testid={`delete-application-${app.id}`} className="text-red-600 hover:text-red-700">
                  <Trash2 size={14} />
                </Button>
              </div>
              <Select value={app.status} onValueChange={(v) => changeStatus(app, v)} disabled={savingStatus}>
                <SelectTrigger className="h-8 text-xs" data-testid={`status-select-${app.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label_en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>
      ))}

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="application-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.full_name || 'Application'}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <StatusBadge status={selected.status} />
                <span className="text-xs text-gray-500">{formatDate(selected.created_at)}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone" value={selected.phone_number} dir="ltr" />
                <Field label="Email" value={selected.email} dir="ltr" />
                {type === 'doctor_pharmacist' ? (
                  <Field label="Specialty" value={selected.specialty} />
                ) : (
                  <Field label="Job Applied For" value={selected.job_title} />
                )}
                <Field label="Highest Qualification" value={selected.highest_qualification} />
                <Field label="Years of Experience" value={selected.years_of_experience} />
                <Field label="Location" value={selected.location || '—'} />
              </div>
              {selected.experience_summary && (
                <div>
                  <div className="text-xs uppercase text-gray-500 mt-2">Experience Summary</div>
                  <div className="p-3 bg-gray-50 rounded text-gray-800 whitespace-pre-wrap">{selected.experience_summary}</div>
                </div>
              )}
              {selected.motivation && (
                <div>
                  <div className="text-xs uppercase text-gray-500 mt-2">Motivation</div>
                  <div className="p-3 bg-gray-50 rounded text-gray-800 whitespace-pre-wrap">{selected.motivation}</div>
                </div>
              )}
              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => downloadCV(selected)} data-testid="dialog-download-cv">
                  <FileText size={14} className="mr-1" /> Download CV
                </Button>
                <Select value={selected.status} onValueChange={(v) => changeStatus(selected, v)}>
                  <SelectTrigger className="w-40 h-9 text-sm" data-testid="dialog-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label_en}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, value, dir }) => (
  <div>
    <div className="text-xs uppercase text-gray-500">{label}</div>
    <div className="font-medium text-gray-800" dir={dir}>{value}</div>
  </div>
);

export const AdminApplicationsView = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState('doctor_pharmacist');

  return (
    <div className="space-y-4" data-testid="admin-applications-view">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#2c3e50]">
          {t('الطلبات', 'Applications')}
        </h2>
      </div>

      <div className="inline-flex bg-gray-100 rounded-xl p-1" data-testid="applications-tabs">
        <button
          onClick={() => setTab('doctor_pharmacist')}
          data-testid="tab-doctor-pharmacist"
          className={`px-4 py-2 rounded-lg text-sm font-bold transition
            ${tab === 'doctor_pharmacist' ? 'bg-white shadow text-[#1e88e5]' : 'text-gray-600 hover:text-gray-800'}`}
        >
          Doctors & Pharmacists
        </button>
        <button
          onClick={() => setTab('job')}
          data-testid="tab-jobs"
          className={`px-4 py-2 rounded-lg text-sm font-bold transition
            ${tab === 'job' ? 'bg-white shadow text-[#1e88e5]' : 'text-gray-600 hover:text-gray-800'}`}
        >
          Jobs
        </button>
      </div>

      <ApplicationsTab type={tab} key={tab} />
    </div>
  );
};

export default AdminApplicationsView;
